import { isAccountPreview } from './previewMode'

export type Auth0Session = {
  id: string
  user_id?: string
  created_at?: string
  updated_at?: string
  authenticated_at?: string
  idle_expires_at?: string
  expires_at?: string
  device?: {
    initial_user_agent?: string
    last_user_agent?: string
    last_ip?: string
    last_asn?: string
  }
  clients?: { client_id: string }[]
}

export type SessionsResponse =
  | { sessions: Auth0Session[] }
  | Auth0Session[]
  | {
      error: string
      message?: string
      hint?: string
      status?: number
      body?: unknown
    }

const base = () =>
  (
    import.meta.env.VITE_ACCOUNT_API_URL ||
    // Preview local da conta: o mock instalado em dev intercepta /api/me/*.
    (isAccountPreview ? window.location.origin : '')
  ).replace(/\/$/, '')

function networkError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) {
    return `Não conseguimos falar com o serviço da conta (${base() || 'URL não configurada'}).`
  }
  return msg
}

export async function listSessions(
  accessToken: string,
): Promise<{ sessions: Auth0Session[]; error?: string; hint?: string }> {
  if (!base()) {
    return {
      sessions: [],
      error: 'Serviço da conta não configurado.',
      hint: 'Defina VITE_ACCOUNT_API_URL no .env da SPA',
    }
  }
  try {
    const res = await fetch(`${base()}/api/me/sessions`, {
      headers: { authorization: `Bearer ${accessToken}` },
    })
    const data = (await res.json().catch(() => ({}))) as SessionsResponse
    if (!res.ok || ('error' in data && data.error)) {
      const err = data as { error?: string; hint?: string; message?: string }
      return {
        sessions: [],
        error: err.message || err.error || `HTTP ${res.status}`,
        hint: err.hint,
      }
    }
    if (Array.isArray(data)) return { sessions: data }
    if ('sessions' in data && Array.isArray(data.sessions)) {
      return { sessions: data.sessions }
    }
    return { sessions: [] }
  } catch (err) {
    return { sessions: [], error: networkError(err) }
  }
}

export async function revokeSession(
  accessToken: string,
  sessionId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(
      `${base()}/api/me/sessions/${encodeURIComponent(sessionId)}`,
      {
        method: 'DELETE',
        headers: { authorization: `Bearer ${accessToken}` },
      },
    )
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      return { ok: false, error: body.error || `HTTP ${res.status}` }
    }
    return { ok: true }
  } catch (err) {
    return { ok: false, error: networkError(err) }
  }
}

export async function revokeAllSessions(
  accessToken: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${base()}/api/me/sessions`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${accessToken}` },
    })
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      return { ok: false, error: body.error || `HTTP ${res.status}` }
    }
    return { ok: true }
  } catch (err) {
    return { ok: false, error: networkError(err) }
  }
}

const BROWSERS: Array<[RegExp, string]> = [
  [/Edg\//i, 'Edge'],
  [/OPR\/|Opera/i, 'Opera'],
  [/Chrome\//i, 'Chrome'],
  [/Firefox\//i, 'Firefox'],
  [/Safari\//i, 'Safari'],
]

const SYSTEMS: Array<[RegExp, string]> = [
  [/Windows/i, 'Windows'],
  [/Android/i, 'Android'],
  [/iPhone|iPad|iOS/i, 'iOS'],
  [/Mac OS X|Macintosh/i, 'macOS'],
  [/Linux/i, 'Linux'],
]

function userAgentOf(session: Auth0Session): string {
  return (
    session.device?.last_user_agent || session.device?.initial_user_agent || ''
  )
}

/** "Chrome no Windows", "Safari no iOS"… — pt-BR, sem nome cru do browser. */
export function formatSessionDevice(session: Auth0Session): string {
  const ua = userAgentOf(session)
  if (!ua) return 'Dispositivo desconhecido'
  const browser = BROWSERS.find(([re]) => re.test(ua))?.[1]
  const system = SYSTEMS.find(([re]) => re.test(ua))?.[1]
  if (browser && system) return `${browser} no ${system}`
  if (browser) return browser
  if (/Mobile/i.test(ua)) return 'Navegador móvel'
  return 'Dispositivo desconhecido'
}

export function isMobileSession(session: Auth0Session): boolean {
  return /Mobile|Android|iPhone|iPad/i.test(userAgentOf(session))
}

const rtf = (() => {
  try {
    return new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })
  } catch {
    return null
  }
})()

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 30],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
]

/** Tempo relativo em pt-BR ("há 5 minutos", "agora mesmo"). */
export function formatRelative(iso?: string, now = Date.now()): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  let seconds = Math.round((then - now) / 1000)
  if (Math.abs(seconds) < 45) return 'agora mesmo'
  let unit: Intl.RelativeTimeFormatUnit = 'second'
  for (const [name, size] of UNITS) {
    if (Math.abs(seconds) < size) {
      unit = name
      break
    }
    seconds = Math.round(seconds / size)
  }
  if (rtf) return rtf.format(seconds, unit)
  return new Date(then).toLocaleString('pt-BR')
}

/** Data+hora completas em pt-BR (detalhe da sessão). */
export function formatDateTime(iso?: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}
